import { apiService } from '@/services/api';
import type { Variaveis } from '@/lib/i18n';

// Handler compartilhado entre apreciacao-edit.tsx (V1) e apreciacao-edit-v2.tsx.
// Persiste os dados de uma parte (todos os riscos dela), atualiza o estado local
// e, quando aplicável, recarrega do backend para obter IDs e URLs de imagens.
//
// Não é um componente nem um hook, então o `t` chega por parâmetro — a mensagem
// do throw sobe até o modal de edição e aparece na tela para o usuário.
export async function salvarParte(params: {
  id: string;
  parteSelecionada: { parte: string; riscos: any[]; numero?: number };
  dadosParte: any;
  riscos: any[];
  setRiscos: (r: any[]) => void;
  onFechar: () => void;
  t: (chave: string, variaveis?: Variaveis) => string;
}): Promise<void> {
  const { id, parteSelecionada, dadosParte, riscos, setRiscos, onFechar, t } = params;
  if (!id || !parteSelecionada) return;

  const novoNomeParte = dadosParte.parte || parteSelecionada.parte;
  const parteAntiga = dadosParte.parteAntiga || parteSelecionada.parte;

  const riscosParaProcessar = parteSelecionada.riscos.length > 0
    ? parteSelecionada.riscos
    : dadosParte.riscos || [];

  for (const risco of riscosParaProcessar) {
    const riscoAtualizado = dadosParte.riscos?.find((r: any) =>
      (r.id && r.id === risco.id) || (r.numero && r.numero === risco.numero)
    ) || risco;

    const tiposPerigoUnicos = new Set<string>();
    if (dadosParte.tiposPerigo && Array.isArray(dadosParte.tiposPerigo)) {
      dadosParte.tiposPerigo.forEach((tp: string) => {
        if (tp && tp.trim()) tiposPerigoUnicos.add(tp.trim());
      });
    }
    const tiposPerigoFinal = Array.from(tiposPerigoUnicos);

    const perigosEncontradosParaSalvar = dadosParte.perigosEncontrados && Array.isArray(dadosParte.perigosEncontrados)
      ? dadosParte.perigosEncontrados.filter((pe: string) => pe && pe.trim() && pe.trim() !== 'teste').join(', ')
      : undefined;

    const dadosAtualizacao: any = {
      parte: novoNomeParte,
      fotoAtualUrl1: dadosParte.fotoParte1 !== undefined ? (dadosParte.fotoParte1 === '' ? null : dadosParte.fotoParte1) : undefined,
      fotoAtualUrl2: dadosParte.fotoParte2 !== undefined ? (dadosParte.fotoParte2 === '' ? null : dadosParte.fotoParte2) : undefined,
      fotoAtualUrl3: dadosParte.fotoParte3 !== undefined ? (dadosParte.fotoParte3 === '' ? null : dadosParte.fotoParte3) : undefined,
      fotoAtualUrl4: dadosParte.fotoParte4 !== undefined ? (dadosParte.fotoParte4 === '' ? null : dadosParte.fotoParte4) : undefined,
      cicloVida: (dadosParte.ciclosVida && dadosParte.ciclosVida.length > 0) ? dadosParte.ciclosVida.join(', ') : null,
      eventoPerigoso: dadosParte.eventoPerigoso !== undefined ? (dadosParte.eventoPerigoso === '' ? null : dadosParte.eventoPerigoso) : undefined,
      conformeAtual: dadosParte.conforme,
      tipoPerigo: tiposPerigoFinal.length > 0 ? tiposPerigoFinal.join(', ') : null,
      perigosEncontrados: perigosEncontradosParaSalvar || null,
      consequencias: dadosParte.potenciaisConsequencias.length > 0 ? dadosParte.potenciaisConsequencias.join(', ') : null,
      potenciaisConsequenciasEspecificas: dadosParte.potenciaisConsequenciasEspecificas && dadosParte.potenciaisConsequenciasEspecificas.length > 0
        ? dadosParte.potenciaisConsequenciasEspecificas
        : null,
      origemEspecifica: dadosParte.origemEspecifica && dadosParte.origemEspecifica.length > 0
        ? dadosParte.origemEspecifica
        : null,
      pronta: dadosParte.pronta,
      probabilidadeAtual: riscoAtualizado.probabilidadeAtual || undefined,
      frequenciaAtual: riscoAtualizado.frequenciaAtual || undefined,
      gravidadeAtual: riscoAtualizado.gravidadeAtual || undefined,
      exposicaoAtual: riscoAtualizado.exposicaoAtual || undefined,
      hrnAtual: riscoAtualizado.hrnAtual || undefined,
      probabilidadeFutura: riscoAtualizado.probabilidadeFuturo !== undefined && riscoAtualizado.probabilidadeFuturo !== null
        ? Number(riscoAtualizado.probabilidadeFuturo)
        : undefined,
      frequenciaFutura: riscoAtualizado.frequenciaFuturo !== undefined && riscoAtualizado.frequenciaFuturo !== null
        ? Number(riscoAtualizado.frequenciaFuturo)
        : undefined,
      gravidadeFutura: riscoAtualizado.gravidadeFuturo !== undefined && riscoAtualizado.gravidadeFuturo !== null
        ? Number(riscoAtualizado.gravidadeFuturo)
        : undefined,
      exposicaoFutura: riscoAtualizado.exposicaoFuturo !== undefined && riscoAtualizado.exposicaoFuturo !== null
        ? Number(riscoAtualizado.exposicaoFuturo)
        : undefined,
      hrnFuturo: riscoAtualizado.hrnFuturo !== undefined && riscoAtualizado.hrnFuturo !== null
        ? Number(riscoAtualizado.hrnFuturo)
        : undefined,
      adequacoesTecnicas: dadosParte.descricaoCondicaoAtual.length > 0 ? dadosParte.descricaoCondicaoAtual.join('\n') : null,
      medidasControle: dadosParte.medidasControle.length > 0 ? dadosParte.medidasControle.join('\n') : null,
      exigenciasNormativas: dadosParte.exigenciasNormativas.length > 0 ? dadosParte.exigenciasNormativas.join('\n') : null,
      adequacoesNormativas: dadosParte.adequacoesNormativas.length > 0 ? dadosParte.adequacoesNormativas.join('\n') : null,
      severidadeSRPCS: riscoAtualizado.severidadeSRPCS || null,
      frequenciaSRPCS: riscoAtualizado.frequenciaSRPCS || null,
      possibilidadeSRPCS: riscoAtualizado.possibilidadeSRPCS || null,
      categoriaSeguranca: riscoAtualizado.categoriaSeguranca || null,
      plr: riscoAtualizado.plr || null,
      sil: riscoAtualizado.sil || null,
      exibirHRN: dadosParte.exibirHRN !== undefined ? dadosParte.exibirHRN : riscoAtualizado.exibirHRN,
      exibirSRPCS: dadosParte.exibirSRPCS !== undefined ? dadosParte.exibirSRPCS : riscoAtualizado.exibirSRPCS,
    };

    // Campos futuros: manter se definidos, remover se undefined/null.
    // Outros campos: remover se undefined.
    Object.keys(dadosAtualizacao).forEach((key) => {
      const camposFuturos = ['probabilidadeFutura', 'frequenciaFutura', 'gravidadeFutura', 'exposicaoFutura', 'hrnFuturo'];
      if (camposFuturos.includes(key)) {
        if (dadosAtualizacao[key] === undefined || dadosAtualizacao[key] === null) {
          delete dadosAtualizacao[key];
        }
      } else if (dadosAtualizacao[key] === undefined) {
        delete dadosAtualizacao[key];
      }
    });

    if (risco.id) {
      const response = await apiService.atualizarRisco(risco.id, dadosAtualizacao);
      if (!response.success) {
        console.error(`Erro ao atualizar risco ${risco.id}:`, response.message);
        throw new Error(response.message || t('apreciacoes.salvar.partes.erroAtualizarRisco'));
      }
    } else {
      const response = await apiService.adicionarRisco(id, {
        ...risco,
        ...dadosAtualizacao,
        numero: risco.numero || 1,
      });
      if (!response.success) {
        console.error('Erro ao adicionar risco:', response.message);
        throw new Error(response.message || t('apreciacoes.salvar.partes.erroAdicionarRisco'));
      }
    }
  }

  // Parte nova: recarregar do backend para obter IDs.
  if (parteSelecionada.riscos.length === 0) {
    const response = await apiService.buscarApreciacao(id);
    if (response.success && response.data) {
      let riscosMapeados = ((response.data as any).riscos || []).map((risco: any) => ({
        ...risco,
        probabilidadeFuturo: risco.probabilidadeFutura,
        frequenciaFuturo: risco.frequenciaFutura,
        gravidadeFuturo: risco.gravidadeFutura,
        exposicaoFuturo: risco.exposicaoFutura,
      }));
      const imagensRes = await apiService.buscarRiscosImagens(id);
      if (imagensRes.success && imagensRes.data && Object.keys(imagensRes.data).length > 0) {
        riscosMapeados = riscosMapeados.map((r: any) => {
          const imgs = imagensRes.data![r.id];
          if (!imgs) return r;
          return {
            ...r,
            fotoAtualUrl1: imgs.fotoAtualUrl1 ?? r.fotoAtualUrl1,
            fotoAtualUrl2: imgs.fotoAtualUrl2 ?? r.fotoAtualUrl2,
            fotoAtualUrl3: imgs.fotoAtualUrl3 ?? r.fotoAtualUrl3,
            fotoAtualUrl4: imgs.fotoAtualUrl4 ?? r.fotoAtualUrl4,
            fotoPlrUrl: imgs.fotoPlrUrl ?? r.fotoPlrUrl,
          };
        });
      }
      setRiscos(riscosMapeados);
    }
    onFechar();
    return;
  }

  // Parte existente: aplicar patch local + sync com backend.
  const novosRiscos = riscos.map((risco) => {
    if (risco.parte === parteAntiga) {
      const riscoAtualizado = dadosParte.riscos.find((r: any) =>
        (r.id && r.id === risco.id) || (r.numero && r.numero === risco.numero)
      ) || risco;

      const tiposPerigoUnicosLocal = new Set<string>();
      if (dadosParte.tiposPerigo && Array.isArray(dadosParte.tiposPerigo)) {
        dadosParte.tiposPerigo.forEach((tp: string) => {
          if (tp && tp.trim()) tiposPerigoUnicosLocal.add(tp.trim());
        });
      }
      const tiposPerigoFinal = Array.from(tiposPerigoUnicosLocal);

      const perigosEncontradosParaSalvar = dadosParte.perigosEncontrados && Array.isArray(dadosParte.perigosEncontrados)
        ? dadosParte.perigosEncontrados.filter((pe: string) => pe && pe.trim() && pe.trim() !== 'teste').join(', ')
        : risco.perigosEncontrados || 'teste';

      return {
        ...risco,
        ...riscoAtualizado,
        parte: novoNomeParte,
        fotoAtualUrl1: dadosParte.fotoParte1,
        fotoAtualUrl2: dadosParte.fotoParte2,
        fotoAtualUrl3: dadosParte.fotoParte3,
        fotoAtualUrl4: dadosParte.fotoParte4,
        cicloVida: dadosParte.ciclosVida.join(', '),
        eventoPerigoso: dadosParte.eventoPerigoso,
        conformeAtual: dadosParte.conforme,
        tipoPerigo: tiposPerigoFinal.length > 0 ? tiposPerigoFinal.join(', ') : risco.tipoPerigo,
        perigosEncontrados: perigosEncontradosParaSalvar,
        consequencias: dadosParte.potenciaisConsequencias.join(', '),
        potenciaisConsequenciasEspecificas: dadosParte.potenciaisConsequenciasEspecificas && dadosParte.potenciaisConsequenciasEspecificas.length > 0
          ? dadosParte.potenciaisConsequenciasEspecificas
          : undefined,
        origemEspecifica: dadosParte.origemEspecifica && dadosParte.origemEspecifica.length > 0
          ? dadosParte.origemEspecifica
          : undefined,
        pronta: dadosParte.pronta,
        probabilidadeAtual: riscoAtualizado.probabilidadeAtual || risco.probabilidadeAtual,
        frequenciaAtual: riscoAtualizado.frequenciaAtual || risco.frequenciaAtual,
        gravidadeAtual: riscoAtualizado.gravidadeAtual || risco.gravidadeAtual,
        exposicaoAtual: riscoAtualizado.exposicaoAtual || risco.exposicaoAtual,
        hrnAtual: riscoAtualizado.hrnAtual || risco.hrnAtual,
        probabilidadeFuturo: riscoAtualizado.probabilidadeFuturo || risco.probabilidadeFuturo,
        frequenciaFuturo: riscoAtualizado.frequenciaFuturo || risco.frequenciaFuturo,
        gravidadeFuturo: riscoAtualizado.gravidadeFuturo || risco.gravidadeFuturo,
        exposicaoFuturo: riscoAtualizado.exposicaoFuturo || risco.exposicaoFuturo,
        hrnFuturo: riscoAtualizado.hrnFuturo || risco.hrnFuturo,
        severidadeSRPCS: riscoAtualizado.severidadeSRPCS || risco.severidadeSRPCS,
        frequenciaSRPCS: riscoAtualizado.frequenciaSRPCS || risco.frequenciaSRPCS,
        possibilidadeSRPCS: riscoAtualizado.possibilidadeSRPCS || risco.possibilidadeSRPCS,
        categoriaSeguranca: riscoAtualizado.categoriaSeguranca || risco.categoriaSeguranca,
        plr: riscoAtualizado.plr || risco.plr,
        sil: riscoAtualizado.sil || risco.sil,
      };
    }
    return risco;
  });

  setRiscos(novosRiscos);

  const response = await apiService.buscarApreciacao(id);
  if (response.success && response.data) {
    let riscosMapeados = ((response.data as any).riscos || []).map((risco: any) => ({
      ...risco,
      probabilidadeFuturo: risco.probabilidadeFutura,
      frequenciaFuturo: risco.frequenciaFutura,
      gravidadeFuturo: risco.gravidadeFutura,
      exposicaoFuturo: risco.exposicaoFutura,
    }));
    const imagensRes = await apiService.buscarRiscosImagens(id);
    if (imagensRes.success && imagensRes.data && Object.keys(imagensRes.data).length > 0) {
      riscosMapeados = riscosMapeados.map((r: any) => {
        const imgs = imagensRes.data![r.id];
        if (!imgs) return r;
        return {
          ...r,
          fotoAtualUrl1: imgs.fotoAtualUrl1 ?? r.fotoAtualUrl1,
          fotoAtualUrl2: imgs.fotoAtualUrl2 ?? r.fotoAtualUrl2,
          fotoAtualUrl3: imgs.fotoAtualUrl3 ?? r.fotoAtualUrl3,
          fotoAtualUrl4: imgs.fotoAtualUrl4 ?? r.fotoAtualUrl4,
          fotoPlrUrl: imgs.fotoPlrUrl ?? r.fotoPlrUrl,
        };
      });
    }
    setRiscos(riscosMapeados);
  }

  onFechar();
}
